import React, { useState } from 'react';
import { PlusCircleIcon } from '@heroicons/react/24/solid';
import { Search, ChevronDown, ChevronRight } from 'lucide-react';
import ModalProfessionalForm from './ModalProfessionalForm';
import CardProfessional from './viewComponents/card_professional';

const UNSPECIFIED_GROUP = 'Unspecified';

const TabHealthProfessionals = ({ professionals, loadProfessionals }) => {
  /** undefined = closed; null = create new; object = view/edit existing */
  const [professionalModal, setProfessionalModal] = useState(undefined);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  /** Specialty keys currently collapsed; empty Set = all expanded (default). */
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());

  const activeOnly = professionals.filter((p) => p.is_active);
  const specialties = [...new Set(activeOnly.map((p) => p.specialty).filter(Boolean))].sort();

  const handleSaved = () => {
    setProfessionalModal(undefined);
    loadProfessionals();
  };

  const handleDeleted = () => {
    loadProfessionals();
  };

  const searchTarget = (p) =>
    `${p.name || ''} ${p.specialty || ''} ${p.location || ''} ${p.notes || ''}`.toLowerCase();

  const visibleProfessionals = professionals
    .filter((p) => (showInactive ? true : p.is_active))
    .filter((p) => searchTarget(p).includes(search.toLowerCase()));

  const inactiveCount = professionals.filter((p) => !p.is_active).length;

  // Group by specialty, alphabetically, unspecified last
  const grouped = visibleProfessionals.reduce((groups, p) => {
    const key = p.specialty || UNSPECIFIED_GROUP;
    if (!groups[key]) groups[key] = [];
    groups[key].push(p);
    return groups;
  }, {});

  const groupNames = Object.keys(grouped).sort((a, b) => {
    if (a === UNSPECIFIED_GROUP) return 1;
    if (b === UNSPECIFIED_GROUP) return -1;
    return a.localeCompare(b);
  });
  groupNames.forEach((name) => grouped[name].sort((a, b) => a.name.localeCompare(b.name)));

  const allCollapsed =
    groupNames.length > 0 && groupNames.every((name) => collapsedGroups.has(name));

  const toggleGroup = (groupName) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) next.delete(groupName);
      else next.add(groupName);
      return next;
    });
  };

  const handleExpandCollapseAll = () => {
    if (allCollapsed) {
      setCollapsedGroups(new Set());
    } else {
      setCollapsedGroups(new Set(groupNames));
    }
  };

  return (
    <>
      {professionalModal !== undefined && (
        <ModalProfessionalForm
          professional={professionalModal}
          specialties={specialties}
          onClose={() => setProfessionalModal(undefined)}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
        />
      )}

      {/* Search + expand/collapse all + inactive toggle */}
      <div className="bg-white mb-4 rounded-2xl shadow p-4 flex flex-col md:flex-row md:items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
            aria-hidden="true"
          />
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full py-1.5 pl-8 pr-2 text-sm bg-white border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
          />
        </div>
        {groupNames.length > 0 && (
          <button
            type="button"
            onClick={handleExpandCollapseAll}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex-shrink-0 px-1 whitespace-nowrap"
          >
            {allCollapsed ? 'Expand all' : 'Collapse all'}
          </button>
        )}
        {inactiveCount > 0 && (
          <label className="flex items-center gap-2 text-sm text-gray-600 flex-shrink-0 pl-1">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300"
            />
            Show inactive ({inactiveCount})
          </label>
        )}
      </div>

      {/* Grouped listing */}
      {groupNames.length === 0 ? (
        <p className="text-gray-500 px-2">No professionals registered yet.</p>
      ) : (
        <div className="space-y-6">
          {groupNames.map((groupName) => {
            const isCollapsed = collapsedGroups.has(groupName);
            const Chevron = isCollapsed ? ChevronRight : ChevronDown;
            return (
              <div key={groupName}>
                <button
                  type="button"
                  onClick={() => toggleGroup(groupName)}
                  className="flex items-center w-full mb-2 px-2 text-left hover:opacity-80"
                  aria-expanded={!isCollapsed}
                >
                  <Chevron className="h-4 w-4 text-gray-500 flex-shrink-0 mr-1.5" aria-hidden="true" />
                  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                    {groupName}
                  </h3>
                  <div className="flex-1 h-px bg-gray-200 ml-3" />
                </button>
                {!isCollapsed && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
                    {grouped[groupName].map((professional) => (
                      <CardProfessional
                        key={professional.id}
                        professional={professional}
                        onClick={() => setProfessionalModal(professional)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add Button */}
      <button
        onClick={() => setProfessionalModal(null)}
        className="fixed bottom-20 right-6 bg-blue-500 hover:bg-blue-600 text-white p-3 rounded-full shadow-lg"
      >
        <PlusCircleIcon className="h-8 w-8" />
      </button>
    </>
  );
};

export default TabHealthProfessionals;
